import { useEffect, useMemo, useState } from 'react'
import { Button, Card, Col, Form, Row } from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { FaTrash } from 'react-icons/fa'
import { GiExitDoor } from 'react-icons/gi'
import { useSearchParams } from 'react-router-dom'

import {
  PermissaoTipo,
  RestricaoSaidaTransitoCatraca,
  RestricaoSaidaTipoAlvo
} from '@core/models/entities'
import { Alert } from '@core/gateways'
import { ValidationError } from '@core/models/errors'
import { setupIntranetV2ApiClient } from '@core/proxies/apis/intranet-v2'
import { RecepcaoService } from '@core/services'
import { useAuth, useFetch } from '@presentation/hooks'
import {
  ActionButton,
  ErrorList,
  Input,
  Label,
  Select,
  SelectApi
} from '@presentation/components/molecules'
import { TablePage } from '@presentation/components/templates'
import { PageProps } from '@presentation/types/components/templates'
import {
  Filter,
  FilterProps,
  TableColumn,
  TableProps
} from '@presentation/types/components/organisms'

import { validationSchema } from './components/shared/validation'
import * as S from './styles'

type RestricaoSaidaFormData = {
  tipo: RestricaoSaidaTipoAlvo | null
  alvos: any[]
  horarioMinimoSaida: string
}

const DEFAULT_UNIDADE_ID = 1

const TIPO_OPTIONS = [
  { label: 'Pessoa', value: 'Pessoa' },
  { label: 'Curso', value: 'Curso' },
  { label: 'Tipo', value: 'PessoaTipo' }
]

const getDefaultValues = (): RestricaoSaidaFormData => ({
  tipo: null,
  alvos: [],
  horarioMinimoSaida: ''
})

const getTipoLabel = (tipo?: RestricaoSaidaTipoAlvo | null) => {
  switch (tipo) {
    case 'Pessoa':
      return 'Pessoa'
    case 'Curso':
      return 'Curso'
    case 'PessoaTipo':
      return 'Tipo'
    default:
      return '-'
  }
}

const getTargetPath = (tipo?: RestricaoSaidaTipoAlvo) => {
  switch (tipo) {
    case 'Pessoa':
      return '/pessoas?sort_by=nome'
    case 'Curso':
      return '/cursos?sort_by=nome'
    case 'PessoaTipo':
      return '/pessoa-tipos?sort_by=descricao'
    default:
      return ''
  }
}

const getTargetSelectKey = (tipo?: RestricaoSaidaTipoAlvo | null) => {
  if (!tipo) return 'restricoes-saida-target-empty'

  return `restricoes-saida-target-${tipo}`
}

const getTargetLabel = (tipo?: RestricaoSaidaTipoAlvo) => {
  switch (tipo) {
    case 'Pessoa':
      return 'Selecione uma ou mais pessoas'
    case 'Curso':
      return 'Selecione um ou mais cursos'
    case 'PessoaTipo':
      return 'Selecione um ou mais tipos'
    default:
      return 'Selecione um ou mais alvos'
  }
}

const getTargetOptionLabel = (tipo?: RestricaoSaidaTipoAlvo) => {
  switch (tipo) {
    case 'Pessoa':
      return (option: any) =>
        option.numero_cartao
          ? `${option.nome} (${option.numero_cartao})`
          : option.nome
    case 'Curso':
      return (option: any) => option.nome
    case 'PessoaTipo':
      return (option: any) => option.descricao
    default:
      return (option: any) => option.nome ?? option.descricao ?? ''
  }
}

const getTargetSearchKey = (tipo?: RestricaoSaidaTipoAlvo) => {
  return tipo === 'PessoaTipo' ? 'descricao' : 'nome'
}

const formatHorarioToRequest = (value: string) => {
  if (!value) return value
  return value.length === 5 ? `${value}:00` : value
}

const formatHorarioToInput = (value: string) => value?.slice(0, 5) ?? ''

const formatHorarioToDisplay = (value: string) => formatHorarioToInput(value)

const resolveTargetId = (target: any) => {
  const rawValue =
    target?.id ??
    target?.value?.id ??
    target?.value?.value ??
    target?.value ??
    target

  const id = Number(rawValue)

  return Number.isFinite(id) && id > 0 ? id : null
}

const buildItemPayload = (tipo: RestricaoSaidaTipoAlvo, target: any) => {
  const id = resolveTargetId(target)

  if (!id) return null

  if (tipo === 'Pessoa') {
    return { pessoaId: id }
  }

  if (tipo === 'Curso') {
    return { cursoId: id }
  }

  return { pessoaTipoId: id }
}

const buildItensPayload = (
  tipo: RestricaoSaidaTipoAlvo,
  targets: any[]
): Array<{
  pessoaId?: number | null
  cursoId?: number | null
  pessoaTipoId?: number | null
}> => {
  const seenTargets = new Set<string>()

  return targets.reduce<
    Array<{
      pessoaId?: number | null
      cursoId?: number | null
      pessoaTipoId?: number | null
    }>
  >((items, target) => {
    const item = buildItemPayload(tipo, target)

    if (!item) return items

    const targetId = item.pessoaId ?? item.cursoId ?? item.pessoaTipoId

    if (!targetId) return items

    const dedupeKey = `${tipo}:${targetId}`

    if (seenTargets.has(dedupeKey)) return items

    seenTargets.add(dedupeKey)
    items.push(item)

    return items
  }, [])
}

const mapItemsToTargets = (restricao: RestricaoSaidaTransitoCatraca) => {
  switch (restricao.tipoAlvo) {
    case 'Pessoa':
      return restricao.itens
        .filter(item => item.pessoaId)
        .map(item => ({
          id: item.pessoaId,
          nome: item.pessoaNome
        }))

    case 'Curso':
      return restricao.itens
        .filter(item => item.cursoId)
        .map(item => ({
          id: item.cursoId,
          nome: item.cursoNome
        }))

    case 'PessoaTipo':
      return restricao.itens
        .filter(item => item.pessoaTipoId)
        .map(item => ({
          id: item.pessoaTipoId,
          descricao: item.pessoaTipoDescricao
        }))

    default:
      return []
  }
}

const getAppliedSummary = (restricao: RestricaoSaidaTransitoCatraca) => {
  const labels = restricao.itens
    .map(item => item.pessoaNome ?? item.cursoNome ?? item.pessoaTipoDescricao)
    .filter(Boolean)

  if (labels.length === 0) return '-'

  return labels.join(', ')
}

const normalizeRestricaoItem = (item: any) => ({
  id: item?.id ?? 0,
  pessoaId: item?.pessoaId ?? item?.pessoa_id ?? null,
  pessoaNome: item?.pessoaNome ?? item?.pessoa_nome ?? null,
  cursoId: item?.cursoId ?? item?.curso_id ?? null,
  cursoNome: item?.cursoNome ?? item?.curso_nome ?? null,
  pessoaTipoId: item?.pessoaTipoId ?? item?.pessoa_tipo_id ?? null,
  pessoaTipoDescricao:
    item?.pessoaTipoDescricao ?? item?.pessoa_tipo_descricao ?? null
})

const normalizeRestricao = (item: any): RestricaoSaidaTransitoCatraca => {
  const itens = Array.isArray(item?.itens)
    ? item.itens.map(normalizeRestricaoItem)
    : []

  return {
    id: item?.id ?? 0,
    unidadeId: item?.unidadeId ?? item?.unidade_id ?? DEFAULT_UNIDADE_ID,
    unidadeNome: item?.unidadeNome ?? item?.unidade_nome ?? '',
    catracaId: item?.catracaId ?? item?.catraca_id ?? null,
    catracaNome: item?.catracaNome ?? item?.catraca_nome ?? null,
    horarioMinimoSaida:
      item?.horarioMinimoSaida ?? item?.horario_minimo_saida ?? '',
    ativo: item?.ativo ?? false,
    quantidadeItens:
      item?.quantidadeItens ?? item?.quantidade_itens ?? itens.length,
    tipoAlvo: item?.tipoAlvo ?? item?.tipo_alvo ?? null,
    itens
  }
}

const RestricoesSaidaPage: React.FC = () => {
  const { hasPermission } = useAuth()
  const [searchParams] = useSearchParams()
  const { data, isFetching, totalItems, fetchData } = useFetch({
    path: '/recepcao/restricoes-saida',
    setupApiClient: setupIntranetV2ApiClient,
    queries: {
      ativo: true
    },
    options: {
      sortBy: 'id',
      sortDir: 'desc'
    }
  })

  const [editingRule, setEditingRule] =
    useState<RestricaoSaidaTransitoCatraca | null>(null)
  const [apiErrors, setApiErrors] = useState<string[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isValid }
  } = useForm<RestricaoSaidaFormData>({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
    defaultValues: getDefaultValues()
  })

  useEffect(() => {
    register('tipo')
    register('alvos')
  }, [register])

  const selectedTipo = watch('tipo')
  const selectedAlvos = watch('alvos') ?? []
  const alvosErrorMessage = (errors.alvos as any)?.message as string | undefined
  const normalizedData = useMemo(
    () => data.map(item => normalizeRestricao(item)),
    [data]
  )
  const selectedTipoFilter = searchParams.get('tipo_alvo')

  const filteredData = useMemo(() => {
    if (!selectedTipoFilter) return normalizedData

    return normalizedData.filter(item => item.tipoAlvo === selectedTipoFilter)
  }, [normalizedData, selectedTipoFilter])

  const pageConfigs: PageProps = {
    title: 'Restrição de Saída por Horário',
    icon: <GiExitDoor />,
    description:
      'Configure regras para impedir a saída do campus antes do horário permitido.'
  }

  const filters: Filter[] = [
    {
      type: {
        value: 'tipo_alvo',
        label: 'Tipo'
      },
      description: {
        type: 'select',
        configs: {
          options: [
            { label: 'Pessoa', value: 'Pessoa' },
            { label: 'Curso', value: 'Curso' },
            { label: 'Tipo', value: 'PessoaTipo' }
          ]
        }
      }
    },
    {
      type: {
        value: 'busca',
        label: 'Aplicado para'
      },
      description: {
        type: 'input',
        configs: {
          type: 'text'
        }
      }
    }
  ]

  const filterConfigs: FilterProps = {
    filters,
    fetchData,
    hasStatus: false
  }

  const onClearForm = () => {
    setEditingRule(null)
    setApiErrors([])
    reset(getDefaultValues())
  }

  const onEdit = (restricao: RestricaoSaidaTransitoCatraca) => {
    setEditingRule(restricao)
    setApiErrors([])
    reset({
      tipo: restricao.tipoAlvo ?? null,
      alvos: mapItemsToTargets(restricao),
      horarioMinimoSaida: formatHorarioToInput(restricao.horarioMinimoSaida)
    })
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleTipoChange = (option: any) => {
    const nextTipo = (option?.value ?? null) as RestricaoSaidaTipoAlvo | null

    setValue('tipo', nextTipo, { shouldValidate: true })
    setValue('alvos', [], { shouldValidate: true })
  }

  const onSubmit = async (formData: RestricaoSaidaFormData) => {
    setIsSubmitting(true)
    setApiErrors([])

    const itens = buildItensPayload(
      formData.tipo as RestricaoSaidaTipoAlvo,
      formData.alvos
    )

    const unidadeId = editingRule?.unidadeId ?? DEFAULT_UNIDADE_ID

    const payload = {
      id: editingRule?.id,
      unidadeId,
      catracaId: null,
      horarioMinimoSaida: formatHorarioToRequest(formData.horarioMinimoSaida),
      ativo: true,
      itens
    }

    try {
      if (editingRule) {
        await RecepcaoService.editarRestricaoSaida(payload)
        Alert.callSuccess({ title: 'Regra atualizada com sucesso.' })
      } else {
        await RecepcaoService.adicionarRestricaoSaida(payload)
        Alert.callSuccess({ title: 'Regra adicionada com sucesso.' })
      }

      onClearForm()
      await fetchData()
    } catch (error) {
      if (error instanceof ValidationError) {
        setApiErrors(error.errors)
      } else {
        Alert.callError({
          title: 'Falha ao salvar regra.',
          description:
            'Tente novamente mais tarde. Caso o problema persista, contate o suporte técnico.'
        })
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const toggleStatus = async (restricao: RestricaoSaidaTransitoCatraca) => {
    try {
      if (restricao.ativo) {
        await RecepcaoService.inativarRestricaoSaida(restricao.id)
        Alert.callSuccess({ title: 'Regra inativada com sucesso.' })
      } else {
        await RecepcaoService.ativarRestricaoSaida(restricao.id)
        Alert.callSuccess({ title: 'Regra ativada com sucesso.' })
      }

      await fetchData()
    } catch (error) {
      if (error instanceof ValidationError) {
        setApiErrors(error.errors)
      } else {
        Alert.callError({
          title: 'Falha ao alterar status.',
          description:
            'Tente novamente mais tarde. Caso o problema persista, contate o suporte técnico.'
        })
      }
    }
  }

  const columns: Array<TableColumn<RestricaoSaidaTransitoCatraca>> = [
    {
      name: 'Tipo',
      selector: item => getTipoLabel(item.tipoAlvo),
      sortable: false,
      center: true,
      grow: 1.1
    },
    {
      name: 'Aplicado para',
      cell: item => (
        <S.SummaryText title={getAppliedSummary(item)}>
          {getAppliedSummary(item)}
        </S.SummaryText>
      ),
      grow: 2.2,
      center: true
    },
    {
      name: 'Quantidade',
      selector: item => item.quantidadeItens,
      cell: item => <S.QuantityBadge>{item.quantidadeItens}</S.QuantityBadge>,
      sortable: true,
      sortField: 'quantidade_itens',
      center: true
    },
    {
      name: 'Horário',
      selector: item => formatHorarioToDisplay(item.horarioMinimoSaida),
      sortable: true,
      sortField: 'horario_minimo_saida',
      center: true
    },
    {
      name: 'Ações',
      cell: item => (
        <S.RowActions>
          {hasPermission(PermissaoTipo.Escrita) && (
            <ActionButton type="edit" onClick={() => onEdit(item)} />
          )}
          {hasPermission(PermissaoTipo.Escrita) && (
            <ActionButton
              type={item.ativo ? 'delete' : 'activate'}
              content={item.ativo ? <FaTrash /> : undefined}
              tooltipText={item.ativo ? 'Inativar' : undefined}
              onClick={() => {
                toggleStatus(item).catch(() => undefined)
              }}
            />
          )}
        </S.RowActions>
      ),
      center: true
    }
  ]

  const tableConfigs: TableProps = {
    columns,
    data: filteredData,
    apiConfigs: {
      totalItems: selectedTipoFilter ? filteredData.length : totalItems,
      fetchData,
      isFetching
    }
  }

  return (
    <TablePage
      pageConfigs={pageConfigs}
      filterConfigs={filterConfigs}
      tableConfigs={tableConfigs}
    >
      <S.FormCard>
        <Card>
          <Card.Body>
            <S.FormHeader>
              <div>
                <S.SectionTitle>
                  {editingRule
                    ? 'Editar Regra de Saída'
                    : 'Nova Regra de Saída'}
                </S.SectionTitle>
                <S.FormHelperText>
                  Selecione um tipo e aplique a regra para um ou mais itens do
                  mesmo tipo.
                </S.FormHelperText>
              </div>
            </S.FormHeader>

            {apiErrors.length > 0 && (
              <ErrorList errors={apiErrors} onHide={() => setApiErrors([])} />
            )}

            <Form onSubmit={handleSubmit(onSubmit)}>
              <Row className="g-3">
                <Col xl={3} md={6}>
                  <Select
                    label="Tipo"
                    isRequired
                    options={TIPO_OPTIONS}
                    value={
                      TIPO_OPTIONS.find(
                        option => option.value === selectedTipo
                      ) ?? null
                    }
                    onChange={handleTipoChange}
                    error={errors.tipo?.message}
                    isClearable
                  />
                </Col>

                <Col xl={5} md={6}>
                  {selectedTipo ? (
                    <>
                      <Label isRequired={true}>Aplicar para</Label>
                      <SelectApi
                        key={getTargetSelectKey(selectedTipo)}
                        path={getTargetPath(selectedTipo)}
                        isMulti
                        value={selectedAlvos}
                        onChange={(value: any) =>
                          setValue('alvos', value ?? [], {
                            shouldValidate: true
                          })
                        }
                        placeholder={getTargetLabel(selectedTipo)}
                        getOptionLabel={getTargetOptionLabel(selectedTipo)}
                        getOptionValue={(option: any) => option.id}
                        searchKey={getTargetSearchKey(selectedTipo)}
                      />
                      {alvosErrorMessage && (
                        <Form.Text className="text-danger">
                          {alvosErrorMessage}
                        </Form.Text>
                      )}
                    </>
                  ) : (
                    <Select
                      label="Aplicar para"
                      isRequired
                      options={[]}
                      value={null}
                      isDisabled
                      placeholder="Selecione um Tipo primeiro"
                      noOptionsMessage={() => 'Selecione um Tipo primeiro'}
                    />
                  )}
                </Col>

                <Col xl={2} md={6}>
                  <Input
                    label="Horário mínimo"
                    name="horarioMinimoSaida"
                    register={register}
                    required
                    type="time"
                    error={errors.horarioMinimoSaida?.message}
                  />
                </Col>
              </Row>

              <S.FooterActions>
                <Button variant="danger" type="button" onClick={onClearForm}>
                  Cancelar
                </Button>

                {hasPermission(PermissaoTipo.Escrita) && (
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={!isValid || isSubmitting}
                  >
                    Salvar Regra
                  </Button>
                )}
              </S.FooterActions>
            </Form>
          </Card.Body>
        </Card>
      </S.FormCard>
    </TablePage>
  )
}

export default RestricoesSaidaPage
